import React, { useEffect, useState, useCallback } from 'react';
import { LanguageProvider } from './i18n';
import { ThemeProvider } from './ThemeContext';
import BottomNav from './components/BottomNav';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import Dashboard from './components/Dashboard';
import EmailList from './components/EmailList';
import EmailDetail from './components/EmailDetail';
import SendFile from './components/SendFile';
import AdminPanel from './components/AdminPanel';
import Mappings from './components/Mappings';
import Login from './components/Login';
import { DashboardSkeleton } from './components/Skeleton';
import { API_BASE, apiFetch } from './config';

const AppContent = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({ received: 0, sent: 0, status: 'Checking...', last_check: null });
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access_token'));

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      const [emailsRes, statsRes] = await Promise.all([
        apiFetch(`${API_BASE}/api/emails`),
        apiFetch(`${API_BASE}/api/stats`),
      ]);
      
      if (emailsRes.status === 401 || statsRes.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_type');
        localStorage.removeItem('web_password');
        setLoading(false);
        return;
      }
      
      const emailsData = await emailsRes.json();
      const statsData = await statsRes.json();
      setEmails(emailsData.emails || emailsData);
      setStats(statsData);
    } catch (err) {
      console.error('API xatosi:', err);
      setStats((prev) => ({ ...prev, status: 'Offline' }));
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Push Notification ruxsatini so'rash
  useEffect(() => {
    if (isAuthenticated && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);

  // WebSocket — yangi xat kelganda darhol yangilash
  useEffect(() => {
    if (!isAuthenticated) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = process.env.NODE_ENV === 'production'
      ? `${protocol}//${window.location.host}/ws`
      : `ws://localhost:8000/ws`;
    
    let ws;
    let reconnectTimer;
    
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_emails') {
            fetchData(); // Yangi xat keldi — ma'lumotlarni yangilash

            // Push Notification (browser)
            if ('Notification' in window && Notification.permission === 'granted') {
              const count = data.count || 1;
              new Notification('Yangi xat keldi!', {
                body: count + ' ta yangi xat qabul qilindi',
                icon: '/manifest.json',
                tag: 'new-email',
                renotify: true,
              });
            }

            // Notification sound
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = 830;
              osc.type = 'sine';
              gain.gain.value = 0.3;
              osc.start();
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
              osc.stop(ctx.currentTime + 0.3);
              setTimeout(() => {
                const osc2 = ctx.createOscillator();
                const gain2 = ctx.createGain();
                osc2.connect(gain2);
                gain2.connect(ctx.destination);
                osc2.frequency.value = 1100;
                osc2.type = 'sine';
                gain2.gain.value = 0.3;
                osc2.start();
                gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                osc2.stop(ctx.currentTime + 0.4);
              }, 150);
            } catch (e) {}
          }
          // Real-time stats yangilash
          if (data.type === 'stats_update' && data.data) {
            setStats(data.data);
          }
        } catch (e) {}
      };
      ws.onclose = () => {
        reconnectTimer = setTimeout(connect, 5000); // 5s dan keyin qayta ulanish
      };
      ws.onerror = () => {
        ws.close();
      };
    };
    
    connect();
    
    return () => {
      if (ws) ws.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [isAuthenticated, fetchData]);

  // PWA Badge — o'qilmagan xatlar sonini ko'rsatish
  useEffect(() => {
    const unreadCount = emails.filter(e => !e.is_read).length;
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount).catch(() => {});
      } else {
        navigator.clearAppBadge().catch(() => {});
      }
    }
    // Tab title da ham ko'rsatish
    document.title = unreadCount > 0 ? `(${unreadCount}) Mail Agent` : 'Mail Agent';
  }, [emails]);

  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
  };

  const handleBackFromDetail = () => {
    setSelectedEmail(null);
  };

  const handleReplyFile = () => {
    setSelectedEmail(null);
    setActiveTab('send');
  };

  const handleTabChange = (tab) => {
    setSelectedEmail(null);
    setActiveTab(tab);
  };

  const renderScreen = () => {
    if (selectedEmail) {
      return (
        <EmailDetail
          email={selectedEmail}
          onBack={handleBackFromDetail}
          onReplyFile={handleReplyFile}
        />
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard stats={stats} onCheckMail={fetchData} />;
      case 'emails':
        return <EmailList emails={emails} onSelect={handleSelectEmail} />;
      case 'mappings':
        return <Mappings />;
      case 'send':
        return <SendFile />;
      case 'admin':
        return <AdminPanel stats={stats} />;
      default:
        return <Dashboard stats={stats} onCheckMail={fetchData} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 relative overflow-hidden">
        <main className="relative z-10 p-4 max-w-lg mx-auto pt-6">
          <DashboardSkeleton />
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => {
      setIsAuthenticated(true);
      setLoading(true);
    }} />;
  }

  return (
    <div className="min-h-screen bg-navy-950 relative overflow-hidden">
      <div className="glow-orb glow-orb-blue animate-float"
           style={{ top: '-5%', left: '-10%', width: '300px', height: '300px' }} />
      <div className="glow-orb glow-orb-cyan animate-float"
           style={{ top: '40%', right: '-15%', width: '250px', height: '250px', animationDelay: '2s' }} />
      <div className="glow-orb glow-orb-emerald animate-float"
           style={{ bottom: '10%', left: '10%', width: '200px', height: '200px', animationDelay: '4s' }} />

      <main className="relative z-10 p-4 max-w-lg mx-auto">
        {renderScreen()}
      </main>

      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      <KeyboardShortcuts
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onBack={handleBackFromDetail}
        onRefresh={fetchData}
      />
    </div>
  );
};

const App = () => (
  <ThemeProvider>
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
